import { useTranslations } from 'next-intl'
import { AppRoutes } from '@repo/web-utils/app-routes'
import { CrownIcon, EditIcon, StorefrontIcon, UserPlus01Icon } from 'primitives/icons'
import { FileDownloadOutlineIcon } from 'primitives/icons/files'
import { PhotosIcon } from 'primitives/icons/navigation'
import { MachineEmbroideryTopicIcon } from 'primitives/icons/topics-icons'
import { useUserHasMyArticles, useUserIsDesigner } from '@repo/cf-user/hooks/use-user-data'
import { addOrReplaceLanguageInUrl } from 'cf-i18n/helpers'
import { useUserLanguage } from '@repo/cf-user/contexts/auth'

import { UserEditIcon } from '@web/components/icons/user-edit'
import { HeartIcon } from '@web/components/icons/heart'
import { SchoolIcon } from '@web/components/icons/school'
import { FeedIcon } from '@web/components/icons/feed'
import { LocalShippingIcon } from '@web/components/icons/local-shipping'
import { ReceiptLongIcon } from '@web/components/icons/receipt-long'
import { KeyIcon } from '@web/components/icons/key'
import { NotificationsIcon } from '@web/components/icons/notifications'
import { CreditCardIcon } from '@web/components/icons/credit-card'
import { AttachMoneyIcon } from '@web/components/icons/attach-money'
import { FontDownloadIcon } from '@web/components/icons/font-download'
import { DashboardIcon } from '@web/components/icons/dashboard'
import { Globe02Icon } from '@web/components/icons/globe-02'
import { QueryStatsIcon } from '@web/components/icons/query-stats'
import { Announcement03Icon } from '@web/components/icons/announcement-03'
import { EyeIcon } from '@web/components/icons/eye'
import { Brush01Icon } from '@web/components/icons/brush-01'
import { SettingsIcon } from '@web/components/icons/settings'
import { useGetDesignerVerificationStatusQuery } from '@web/gen/user'
import { DesignerVerificationStatus } from '@web/gen/types'
import { calculateDesignerVerificationCooldown } from '@web/utils/designer-verification-utils'

interface MyAccountNavigationSection {
  title: string
  items: MyAccountNavigationItem[]
}

interface MyAccountNavigationItem {
  title: string
  description: string
  path: string
  icon: React.FC<{ className?: string }>
}
export const useMyAccountNavigationSections = (): MyAccountNavigationSection[] => {
  const t = useTranslations('pages.my_account.sidebar')
  const userLanguage = useUserLanguage()
  const hasMyArticles = useUserHasMyArticles()
  const isDesigner = useUserIsDesigner()
  const { data: designerVerificationStatus } = useGetDesignerVerificationStatusQuery()

  const { cooldownPassed } = calculateDesignerVerificationCooldown(
    designerVerificationStatus?.getDesignerVerificationStatus || null,
  )

  const mainSections = [
    {
      title: t('nav_sections.main.title'),
      items: [
        {
          title: t('nav_sections.main.items.edit_profile.title'),
          description: t('nav_sections.main.items.edit_profile.description'),
          path: `${addOrReplaceLanguageInUrl(AppRoutes.MY_ACCOUNT_PROFILE, userLanguage)}`,
          icon: UserEditIcon,
        },
        {
          title: t('nav_sections.main.items.favourites.title'),
          description: t('nav_sections.main.items.favourites.description'),
          path: `${addOrReplaceLanguageInUrl(AppRoutes.MY_ACCOUNT_FAVORITES, userLanguage)}`,
          icon: HeartIcon,
        },
        {
          title: t('nav_sections.main.items.follow_designers.title'),
          description: t('nav_sections.main.items.follow_designers.description'),
          path: `${addOrReplaceLanguageInUrl(AppRoutes.FOLLOWING, userLanguage)}`,
          icon: UserPlus01Icon,
        },
        {
          title: t('nav_sections.main.items.my_classes.title'),
          description: t('nav_sections.main.items.my_classes.description'),
          path: `${addOrReplaceLanguageInUrl(AppRoutes.MY_ACCOUNT_CLASSES, userLanguage)}`,
          icon: SchoolIcon,
        },
        {
          title: t('nav_sections.main.items.downloads_and_license_keys.title'),
          description: t('nav_sections.main.items.downloads_and_license_keys.description'),
          path: `${addOrReplaceLanguageInUrl(AppRoutes.MY_ACCOUNT_LICENSES, userLanguage)}`,
          icon: FileDownloadOutlineIcon,
        },
        ...(hasMyArticles
          ? [
              {
                title: t('nav_sections.main.items.my_articles.title'),
                description: t('nav_sections.main.items.my_articles.description'),
                path: `${addOrReplaceLanguageInUrl(AppRoutes.MY_ACCOUNT_ARTICLES, userLanguage)}`,
                icon: FeedIcon,
              },
            ]
          : []),
      ],
    },
    {
      title: t('nav_sections.account.title'),
      items: [
        {
          title: t('nav_sections.account.items.edit_email_password.title'),
          description: t('nav_sections.account.items.edit_email_password.description'),
          path: `${addOrReplaceLanguageInUrl(AppRoutes.MY_ACCOUNT_EDIT_ACCOUNT, userLanguage)}`,
          icon: EditIcon,
        },
        {
          title: t('nav_sections.account.items.shipping_address.title'),
          description: t('nav_sections.account.items.shipping_address.description'),
          path: `${addOrReplaceLanguageInUrl(AppRoutes.MY_ACCOUNT_SHIPPING_ADDRESS, userLanguage)}`,
          icon: LocalShippingIcon,
        },
        {
          title: t('nav_sections.account.items.orders_statements.title'),
          description: t('nav_sections.account.items.orders_statements.description'),
          path: `${addOrReplaceLanguageInUrl(AppRoutes.MY_ACCOUNT_ORDERS, userLanguage)}`,
          icon: ReceiptLongIcon,
        },
        {
          title: t('nav_sections.account.items.social_login_accounts.title'),
          description: t('nav_sections.account.items.social_login_accounts.description'),
          path: `${addOrReplaceLanguageInUrl(AppRoutes.MY_ACCOUNT_SOCIAL_LOGIN, userLanguage)}`,
          icon: KeyIcon,
        },
        {
          title: t('nav_sections.account.items.notifications.title'),
          description: t('nav_sections.account.items.notifications.description'),
          path: `${addOrReplaceLanguageInUrl(
            AppRoutes.MY_ACCOUNT_NOTIFICATION_SETTINGS,
            userLanguage,
          )}`,
          icon: NotificationsIcon,
        },
      ],
    },
    {
      title: t('nav_sections.payment.title'),
      items: [
        {
          title: t('nav_sections.payment.items.billing_details.title'),
          description: t('nav_sections.payment.items.billing_details.description'),
          path: `${addOrReplaceLanguageInUrl(AppRoutes.MY_ACCOUNT_BILLING_DETAILS, userLanguage)}`,
          icon: CreditCardIcon,
        },
        {
          title: t('nav_sections.payment.items.subscriptions.title'),
          description: t('nav_sections.payment.items.subscriptions.description'),
          path: `${addOrReplaceLanguageInUrl(AppRoutes.MY_ACCOUNT_SUBSCRIPTIONS, userLanguage)}`,
          icon: CrownIcon,
        },
        {
          title: t('nav_sections.payment.items.payment_methods.title'),
          description: t('nav_sections.payment.items.payment_methods.description'),
          path: `${addOrReplaceLanguageInUrl(AppRoutes.MY_ACCOUNT_PAYMENT_METHODS, userLanguage)}`,
          icon: AttachMoneyIcon,
        },
      ],
    },
  ]

  // Designer section - conditionally added based on isDesigner status
  if (isDesigner) {
    mainSections.push({
      title: t('nav_sections.designer.title'),
      items: [
        {
          title: t('nav_sections.designer.items.add_font.title'),
          description: t('nav_sections.designer.items.add_font.description'),
          path: `${addOrReplaceLanguageInUrl(AppRoutes.MY_ACCOUNT_ADD_FONT, userLanguage)}`,
          icon: FontDownloadIcon,
        },
        {
          title: t('nav_sections.designer.items.add_graphic.title'),
          description: t('nav_sections.designer.items.add_graphic.description'),
          path: `${addOrReplaceLanguageInUrl(AppRoutes.MY_ACCOUNT_ADD_GRAPHIC, userLanguage)}`,
          icon: PhotosIcon,
        },
        {
          title: t('nav_sections.designer.items.add_embroidery.title'),
          description: t('nav_sections.designer.items.add_embroidery.description'),
          path: `${addOrReplaceLanguageInUrl(AppRoutes.MY_ACCOUNT_ADD_EMBROIDERY, userLanguage)}`,
          icon: MachineEmbroideryTopicIcon,
        },
        {
          title: t('nav_sections.designer.items.view_dashboard.title'),
          description: t('nav_sections.designer.items.view_dashboard.description'),
          path: `${addOrReplaceLanguageInUrl(AppRoutes.DASHBOARD, userLanguage)}`,
          icon: DashboardIcon,
        },
      ],
    })
  } else {
    const status = designerVerificationStatus?.getDesignerVerificationStatus?.status
    const isRejected = status === DesignerVerificationStatus.Rejected

    if (
      // Display "Open a store" item for non-designers if they are:
      // 1. Soft rejected
      // 2. Not verified (undefined status)
      // 3. Rejected but cooldown period has passed
      status === DesignerVerificationStatus.SoftRejected ||
      status === undefined ||
      (isRejected && cooldownPassed)
    ) {
      // Add "Open a store" item for non-designers
      mainSections.push({
        title: t('nav_sections.designer.title'),
        items: [
          {
            title: t('nav_sections.designer.items.open_store.title'),
            description: t('nav_sections.designer.items.open_store.description'),
            path: '/open-store',
            icon: StorefrontIcon,
          },
        ],
      })
    }
  }

  // Add affiliate section
  mainSections.push({
    title: t('nav_sections.affiliate.title'),
    items: [
      {
        title: t('nav_sections.affiliate.items.affiliate_dashboard.title'),
        description: t('nav_sections.affiliate.items.affiliate_dashboard.description'),
        path: `${addOrReplaceLanguageInUrl(AppRoutes.AFFILIATE_AREA, userLanguage)}`,
        icon: DashboardIcon,
      },
      {
        title: t('nav_sections.affiliate.items.referral_url_generator.title'),
        description: t('nav_sections.affiliate.items.referral_url_generator.description'),
        path: `${addOrReplaceLanguageInUrl(AppRoutes.REFERRAL_URL_GENERATOR, userLanguage)}`,
        icon: Globe02Icon,
      },
      {
        title: t('nav_sections.affiliate.items.statistics.title'),
        description: t('nav_sections.affiliate.items.statistics.description'),
        path: `${addOrReplaceLanguageInUrl(AppRoutes.AFFILIATE_AREA_STATS, userLanguage)}`,
        icon: QueryStatsIcon,
      },
      {
        title: t('nav_sections.affiliate.items.referrals.title'),
        description: t('nav_sections.affiliate.items.referrals.description'),
        path: `${addOrReplaceLanguageInUrl(AppRoutes.AFFILIATE_AREA_REFERRALS, userLanguage)}`,
        icon: Announcement03Icon,
      },
      {
        title: t('nav_sections.affiliate.items.visits.title'),
        description: t('nav_sections.affiliate.items.visits.description'),
        path: `${addOrReplaceLanguageInUrl(AppRoutes.AFFILIATE_AREA_VISITS, userLanguage)}`,
        icon: EyeIcon,
      },
      {
        title: t('nav_sections.affiliate.items.banners_and_creatives.title'),
        description: t('nav_sections.affiliate.items.banners_and_creatives.description'),
        path: `${addOrReplaceLanguageInUrl(AppRoutes.AFFILIATE_AREA_CREATIVES, userLanguage)}`,
        icon: Brush01Icon,
      },
      {
        title: t('nav_sections.affiliate.items.affiliate_settings.title'),
        description: t('nav_sections.affiliate.items.affiliate_settings.description'),
        path: `${addOrReplaceLanguageInUrl(AppRoutes.AFFILIATE_AREA_SETTINGS, userLanguage)}`,
        icon: SettingsIcon,
      },
    ],
  })

  return mainSections
}
